/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useStore } from '~src/store';
import { DownloadingState } from '~src/store';
import { Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Progress } from 'antd';
import 'antd/dist/antd.css';

interface FileProgressPanelProps {
  mobile?: boolean;
}

/** FileProgressPanelDownload */
export const FileProgressPanelDownload: React.FC<FileProgressPanelProps> = ({
  mobile,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const {
    isDownloadingActive,
    downloadingState,
    changeDownloadingActive,
    isOfferDownload,
    totalFoldersNumber,
    totalFilesNumber,
    totalSizeDownload,
    currentFileDownloaded,
    totalFileSize,
    changeDownloadingState,
    errorList,
    clearErrors,
  } = useStore();

  const getHeaderTranslation = () => {
    if (downloadingState === DownloadingState.PREPARING) {
      return 'panel-download-preparing';
    }

    if (downloadingState === DownloadingState.FETCHING) {
      return 'panel-fetching-starting';
    }

    if (downloadingState === DownloadingState.DOWNLOADING) {
      return 'panel-downloading-starting';
    }

    if (downloadingState === DownloadingState.FINISHING) {
      return 'panel-download-finishing';
    }
  };

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log10(bytes) / 3);
    const size = (bytes / Math.pow(1024, i)).toFixed(2);

    return `${size} ${sizes[i]}`;
  }

  const handleCloseClick = () => {
    changeDownloadingActive(false);
    changeDownloadingState('FINISHING');
    clearErrors();
  };

  const iconHeader =
    downloadingState === DownloadingState.PREPARING
      ? ['fas', 'loading']
      : downloadingState === DownloadingState.DOWNLOADING
      ? ['fas', 'downloading']
      : ['fas', 'check'];

  const IconSpin = <FontAwesomeIcon icon={['far', 'spinner-third']} spin />;

  const calculatePercentage = (current, total) => {
    if (total === 0) return 0;
    const rawPercentage = (current / total) * 100;
    return rawPercentage % 1 === 0
      ? rawPercentage
      : parseFloat(rawPercentage.toFixed(2));
  };

  const percentage = calculatePercentage(
    currentFileDownloaded,
    totalFilesNumber
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDownloadingActive) {
        event.preventDefault();
        event.returnValue = intl.formatMessage({ id: 'refresh-page-warning' });
      }
    };

    if (totalFilesNumber > 1) {
      if (isDownloadingActive) {
        window.addEventListener('beforeunload', handleBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [totalFilesNumber, isDownloadingActive]);

  return (
    <Fragment>
      {isDownloadingActive && (
        <Fragment>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              pointerEvents: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}>
            <div style={{ width: '3%', height: '3%' }}>
              <FontAwesomeIcon
                icon={['far', 'lock']}
                style={{ color: 'white', width: '100%', height: '100%' }}
              />
            </div>
          </div>
          {/* New Error List Display */}
          {errorList.length > 0 && (
            <div css={styles.errorBox}>
              <strong>{intl.formatMessage({ id: 'errors' })}:</strong>
              <ul>
                {errorList.map((error, index) => (
                  <li key={index} style={{ color: 'red' }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div css={[styles.container, mobile && { bottom: 100 }]}>
            <div css={styles.header}>
              <div css={styles.headerTextContainer}>
                <div css={styles.headerText}>
                  {downloadingState === DownloadingState.PREPARING && (
                    <Spin indicator={IconSpin} />
                  )}
                  {downloadingState === DownloadingState.FETCHING && (
                    <FontAwesomeIcon
                      icon={['far', 'download']}
                      style={{ color: 'red' }}
                    />
                  )}
                  {downloadingState === DownloadingState.DOWNLOADING && (
                    <Spin indicator={IconSpin} />
                  )}
                  {downloadingState === DownloadingState.FINISHING && (
                    <FontAwesomeIcon
                      icon={['far', 'check']}
                      style={{ color: 'green' }}
                    />
                  )}
                  {intl.formatMessage({ id: getHeaderTranslation() })}
                </div>
              </div>
              <div css={{ pointerEvents: 'all' }}>
                <IconButton
                  icon={['far', 'times']}
                  click={handleCloseClick}
                  tooltip={intl.formatMessage({ id: 'close' })}
                />
              </div>
            </div>
            <div css={styles.contentContainer}>
              {downloadingState === DownloadingState.PREPARING && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {!isOfferDownload && (
                    <Fragment>
                      <p>
                        {intl.formatMessage({ id: 'total-files' })}:{' '}
                        {totalFilesNumber}
                      </p>
                      <p>
                        {intl.formatMessage({ id: 'total-folders' })}:{' '}
                        {totalFoldersNumber}
                      </p>
                    </Fragment>
                  )}
                  {isOfferDownload && (
                    <p
                      style={{
                        margin: 0,
                        textAlign: 'center',
                        padding: '15px 0',
                      }}>
                      {intl.formatMessage({ id: 'panel-download-preparing' })}
                    </p>
                  )}
                </div>
              )}

              {downloadingState === DownloadingState.FETCHING && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%',
                  }}>
                  {!isOfferDownload && (
                    <Fragment>
                      <p>
                        {currentFileDownloaded} | {totalFilesNumber}
                      </p>
                      <div
                        style={{
                          paddingBottom: '30px',
                        }}>
                        <div
                          style={{
                            margin: '0 auto',
                            width: '150px',
                          }}>
                          <Progress percent={percentage} status="active" />
                        </div>
                      </div>
                      {totalFileSize !== 0 && (
                        <p
                          style={{
                            fontSize: '12px',
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                          }}>
                          {intl.formatMessage({ id: 'total-size' })}:{' '}
                          {formatBytes(totalFileSize)}
                        </p>
                      )}
                    </Fragment>
                  )}
                  {isOfferDownload && (
                    <p
                      style={{
                        margin: 0,
                        textAlign: 'center',
                        padding: '15px 0',
                      }}>
                      {intl.formatMessage({ id: 'panel-fetching-starting' })}
                    </p>
                  )}
                </div>
              )}

              {downloadingState === DownloadingState.DOWNLOADING && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '20px',
                  }}>
                  {!isOfferDownload && (
                    <Fragment>
                      <p>{totalSizeDownload}%</p>
                      <p>
                        {intl.formatMessage({
                          id: 'panel-downloading-starting',
                        })}
                      </p>
                      {totalFileSize !== 0 && (
                        <p
                          style={{
                            fontSize: '12px',
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                          }}>
                          {intl.formatMessage({ id: 'size' })}:{' '}
                          {formatBytes(totalFileSize)}
                        </p>
                      )}
                    </Fragment>
                  )}
                  {isOfferDownload && (
                    <p style={{ margin: 0, textAlign: 'center' }}>
                      {intl.formatMessage({ id: 'panel-downloading-starting' })}
                    </p>
                  )}
                </div>
              )}

              {downloadingState === DownloadingState.FINISHING && (
                <div style={{ padding: '15px 0' }}>
                  {intl.formatMessage({ id: 'logs' })} &gt;&gt;
                </div>
              )}
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  errorBox: {
    padding: '10px',
    marginTop: '10px',
    maxHeight: '100px',
    overflowY: 'auto',
    width: 300,
    borderRadius: 6,

    position: 'fixed',
    bottom: 170,
    right: 20,
    zIndex: 1001,
    backgroundColor: '#ffdddd',
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.21)',
  },
  container: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    backgroundColor: theme.colors.white,
    padding: '0 13px',
    width: 300,
    borderRadius: 6,
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.21)',
    zIndex: 1111,
  },
  header: {
    padding: '10px 0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 16,
  },
  headerTextContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerText: {
    display: 'flex',
    fontWeight: 'bold',
    gap: '10px',
  },
  list: {
    overflowY: 'auto',
    transition: 'all 0.2s ease-out',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
